import axios, { AxiosError, AxiosInstance } from 'axios';
import { auth } from '../config/firebase';
import { nanoid } from 'nanoid';
import { mergeInvoices } from '../utils/invoiceMerge';
import type {
  Invoice,
  Settings,
  ListInvoicesResponse,
  Schedule,
  CompanyData
} from '@dutchinvoice/types';
import type { SyncedInvoice } from '../types/invoice';
import { isFutureDate } from '@dutchinvoice/shared-utils';

// Use environment variable with fallback
const BASE_URL = import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV
    ? 'http://127.0.0.1:8787'
    : 'https://api.invoicehappens.com');

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Offline queue
interface QueuedOperation {
  id: string;
  type: 'create' | 'update' | 'delete' | 'bulk';
  endpoint: string;
  data: Record<string, unknown>;
  retryCount: number;
}

const QUEUE_KEY = 'dutchinvoice_offline_queue';
const MAX_RETRIES = 3;

let isRefreshing = false;
let failedQueue: { resolve: (value: unknown) => void; reject: (reason?: unknown) => void; }[] = [];

const getOfflineQueue = (): QueuedOperation[] => {
  const stored = localStorage.getItem(QUEUE_KEY);
  return stored ? JSON.parse(stored) : [];
};

const saveOfflineQueue = (queue: QueuedOperation[]) => {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
};

const queueOfflineOperation = (operation: Omit<QueuedOperation, 'id' | 'retryCount'>) => {
  const id = nanoid();
  const queueItem: QueuedOperation = {
    ...operation,
    id,
    retryCount: 0
  };
  const queue = getOfflineQueue();
  queue.push(queueItem);
  saveOfflineQueue(queue);
  return queueItem;
};

const processQueue = async (error: Error | null = null) => {
  const currentQueue = [...failedQueue];
  failedQueue = [];

  currentQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(undefined);
    }
  });
};

// Local storage functions
const getLocalInvoices = (): Invoice[] => {
  const stored = localStorage.getItem('dutchinvoice_local_invoices');
  return stored ? JSON.parse(stored) : [];
};

const saveLocalInvoices = (invoices: Invoice[]) => {
  localStorage.setItem('dutchinvoice_local_invoices', JSON.stringify(invoices));
};

const getLocalSettings = (): Settings | null => {
  const stored = localStorage.getItem('dutchinvoice_local_settings');
  return stored ? JSON.parse(stored) : null;
};

const saveLocalSettings = (settings: Settings) => {
  localStorage.setItem('dutchinvoice_local_settings', JSON.stringify(settings));
};

// Token refresh interceptor
api.interceptors.request.use(
  async config => {
    if (!config.headers.Authorization) {
      const token = await auth.currentUser?.getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  error => Promise.reject(error)
);

api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest?.headers['X-Retry']) {
      if (!isRefreshing) {
        isRefreshing = true;

        try {
          await auth.currentUser?.getIdToken(true);
          isRefreshing = false;
          processQueue();

          // Retry original request with new token
          if (originalRequest) {
            originalRequest.headers['X-Retry'] = 'true';
            return api(originalRequest);
          }
        } catch (refreshError) {
          isRefreshing = false;
          if (refreshError instanceof Error) {
            processQueue(refreshError);
          } else {
            processQueue(new Error('Unknown refresh error'));
          }
          return Promise.reject(refreshError);
        }
      }

      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      });
    }

    return Promise.reject(error);
  }
);

// Offline support
const processOfflineQueue = async () => {
  if (!navigator.onLine) return;

  const queue = getOfflineQueue();
  const remainingQueue: QueuedOperation[] = [];

  for (const operation of queue) {
    try {
      switch (operation.type) {
        case 'create':
          await api.post(operation.endpoint, operation.data);
          break;
        case 'update':
          await api.put(operation.endpoint, operation.data);
          break;
        case 'delete':
          await api.delete(operation.endpoint);
          break;
        case 'bulk':
          await api.post(operation.endpoint, operation.data);
          break;
      }
      // Operation succeeded, don't add to remaining queue
    } catch { // Ignore errors during retry handling
      if (operation.retryCount >= MAX_RETRIES) {
        // Max retries reached, don't add to remaining queue
      } else {
        // Add to remaining queue with incremented retry count
        remainingQueue.push({ ...operation, retryCount: operation.retryCount + 1 });
      }
    }
  }

  saveOfflineQueue(remainingQueue);
};

// API functions with local-first approach
export const invoiceApi = {
  async sync(options?: { period?: string }) {
    try {
      let url = '/api/invoices';

      // Add period filtering if provided
      if (options?.period) {
        url += `?period=${encodeURIComponent(options.period)}`;
      }

      const response = await api.get<ListInvoicesResponse>(url);
      const localInvoices = getLocalInvoices();
      // Type assertion needed because the Invoice type has been updated
      const mergedInvoices = mergeInvoices(response.data.invoices, localInvoices as unknown as SyncedInvoice[], { debug: true });
      saveLocalInvoices(mergedInvoices);

      // Filter for planned invoices (template invoices with schedules or future invoices)
      if (options?.period === 'planned') {
        return mergedInvoices.filter(inv => {
          // Exclude generated invoices (they are actual invoices, not schedule templates)
          if (inv.generatedFromScheduleId) {
            return false;
          }

          const isFutureInvoice = isFutureDate(inv.date);

          // Check if it has a scheduleId (template invoice)
          const hasScheduleId = !!inv.scheduleId;

          return isFutureInvoice || hasScheduleId;
        });
      }

      return mergedInvoices;
    } catch (error) {
      console.error('Failed to sync invoices:', error);
      return getLocalInvoices();
    }
  },

  async getInvoices(options?: {
    page?: number;
    limit?: number;
    status?: string;
    period?: string;
    startDate?: string;
    endDate?: string;
    filter?: string;
  }) {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (options?.page) params.append('page', options.page.toString());
      if (options?.limit) params.append('limit', options.limit.toString());
      if (options?.status) params.append('status', options.status);
      if (options?.period) params.append('period', options.period);
      if (options?.startDate) params.append('startDate', options.startDate);
      if (options?.endDate) params.append('endDate', options.endDate);
      if (options?.filter) params.append('filter', options.filter);

      const queryString = params.toString();
      const url = `/api/invoices${queryString ? `?${queryString}` : ''}`;

      const response = await api.get<ListInvoicesResponse>(url);
      return response.data;
    } catch (error) {
      console.error('Failed to get invoices:', error);

      // Return local invoices with pagination
      const localInvoices = getLocalInvoices();
      const page = options?.page || 1;
      const limit = options?.limit || 50;
      const start = (page - 1) * limit;
      const end = start + limit;

      // Apply local filtering if needed
      let filteredInvoices = localInvoices;

      // Filter by status if provided
      if (options?.status) {
        filteredInvoices = filteredInvoices.filter(inv => inv.status === options.status);
      }

      // Filter by planned if provided
      if (options?.filter === 'planned') {
        filteredInvoices = filteredInvoices.filter(inv => {
          // Exclude generated invoices (they are actual invoices, not schedule templates)
          if (inv.generatedFromScheduleId) {
            return false;
          }

          // Check if it's a future invoice
          const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
          const isFutureInvoice = inv.date > today;

          // Check if it has a scheduleId (template invoice)
          const hasScheduleId = !!inv.scheduleId;

          return isFutureInvoice || hasScheduleId;
        });
      }

      // Filter by date range if provided
      if (options?.startDate && options?.endDate) {
        filteredInvoices = filteredInvoices.filter(inv => {
          const invoiceDate = inv.date;
          return invoiceDate >= options.startDate! && invoiceDate <= options.endDate!;
        });
      } else if (options?.period) {
        // Handle period filtering locally
        if (options.period.startsWith('month-')) {
          // Month filter: month-YYYY-MM
          const [, yearStr, monthStr] = options.period.split('-');
          const year = parseInt(yearStr, 10);
          const month = parseInt(monthStr, 10);

          if (!isNaN(year) && !isNaN(month)) {
            const firstDay = `${year}-${month.toString().padStart(2, '0')}-01`;

            // Last day calculation
            let lastDay: string;
            if (month === 12) {
              lastDay = `${year}-12-31`;
            } else {
              const nextMonth = month + 1;
              const nextMonthYear = nextMonth > 12 ? year + 1 : year;
              const nextMonthPadded = (nextMonth > 12 ? 1 : nextMonth).toString().padStart(2, '0');
              const lastDayDate = new Date(`${nextMonthYear}-${nextMonthPadded}-01`);
              lastDayDate.setDate(lastDayDate.getDate() - 1);

              const lastDayMonth = (lastDayDate.getMonth() + 1).toString().padStart(2, '0');
              const lastDayDay = lastDayDate.getDate().toString().padStart(2, '0');
              lastDay = `${lastDayDate.getFullYear()}-${lastDayMonth}-${lastDayDay}`;
            }

            filteredInvoices = filteredInvoices.filter(inv => {
              const invoiceDate = inv.date;
              return invoiceDate >= firstDay && invoiceDate <= lastDay;
            });
          }
        } else if (options.period.startsWith('custom-')) {
          // Custom date range: custom-YYYY-MM-DD-YYYY-MM-DD
          const parts = options.period.split('-');
          if (parts.length === 7) {
            const customStartDate = `${parts[1]}-${parts[2]}-${parts[3]}`;
            const customEndDate = `${parts[4]}-${parts[5]}-${parts[6]}`;

            filteredInvoices = filteredInvoices.filter(inv => {
              const invoiceDate = inv.date;
              return invoiceDate >= customStartDate && invoiceDate <= customEndDate;
            });
          }
        }
      }

      // Apply pagination
      const paginatedInvoices = filteredInvoices.slice(start, end);

      return {
        invoices: paginatedInvoices,
        total: filteredInvoices.length,
        page,
        limit,
        hasMore: end < filteredInvoices.length
      };
    }
  },

  async create(invoice: Invoice) {
    // Save locally first
    const localInvoices = getLocalInvoices();
    const newInvoice = { ...invoice, id: nanoid() };

    localInvoices.push(newInvoice);
    saveLocalInvoices(localInvoices);

    // Then sync to cloud if online and authenticated
    if (navigator.onLine && auth.currentUser) {
      try {
        // No need to get settings anymore since we're fully migrated

        // Wrap the invoice in an object with 'invoice' property as expected by the backend
        const response = await api.post('/api/invoices', { invoice });
        return response.data;
      } catch (error) {
        console.error('Failed to create invoice:', error);

        // Queue for retry
        queueOfflineOperation({
          type: 'create',
          endpoint: '/api/invoices',
          data: newInvoice as Record<string, unknown>
        });
        throw error;
      }
    }

    return newInvoice;
  },

  async update(invoice: Invoice) {
    try {
      // Check if we're online
      if (!navigator.onLine) {

        queueOfflineOperation({
          type: 'update',
          endpoint: `/api/invoices/${invoice.id}`,
          data: invoice as unknown as Record<string, unknown>
        });
        return invoice;
      }

      // Wrap the invoice in an object with 'invoice' property as expected by the backend
      const response = await api.put<Invoice>(`/api/invoices/${invoice.id}`, { invoice });

      // Update local storage
      const localInvoices = getLocalInvoices();
      const updatedInvoices = localInvoices.map(inv =>
        inv.id === invoice.id ? { ...response.data, synced: true } : inv
      );
      saveLocalInvoices(updatedInvoices);

      return response.data;
    } catch (error) {
      console.error('Failed to update invoice:', error);
      throw error;
    }
  },

  async delete(invoiceId: string) {
    // Delete locally first
    const localInvoices = getLocalInvoices();
    const updatedInvoices = localInvoices.filter(inv => inv.id !== invoiceId);
    saveLocalInvoices(updatedInvoices);

    // Then sync to cloud if online and authenticated
    if (navigator.onLine && auth.currentUser) {
      try {
        await api.delete(`/api/invoices/${invoiceId}`);
      } catch (error) {
        // Queue for retry if offline or error
        queueOfflineOperation({
          type: 'delete',
          endpoint: `/api/invoices/${invoiceId}`,
          data: {} as Record<string, unknown>
        });
        throw error;
      }
    }
  },

  async sendInvoiceEmail(invoiceId: string, recipientEmail: string, customMessage?: string, locale?: string) {
    try {
      const response = await api.post(`/api/invoices/${invoiceId}/send-email`, {
        recipientEmail,
        customMessage,
        locale
      });
      return response.data;
    } catch (error) {
      console.error('Failed to send invoice email:', error);
      throw error;
    }
  },

  async getInvoiceEmails(invoiceId: string) {
    try {
      const response = await api.get(`/api/invoices/${invoiceId}/emails`);
      return response.data;
    } catch (error) {
      console.error('Failed to get invoice emails:', error);
      throw error;
    }
  },

  async sendInvoiceReminder(invoiceId: string, recipientEmail: string, customMessage?: string, locale?: string) {
    try {
      const response = await api.post(`/api/invoices/${invoiceId}/send-reminder`, {
        recipientEmail,
        customMessage,
        locale
      });
      return response.data;
    } catch (error) {
      console.error('Failed to send invoice reminder:', error);
      throw error;
    }
  },

  async bulkCreate(invoices: Invoice[]) {
    // Save locally first
    const localInvoices = getLocalInvoices();
    const newInvoices = invoices.map(invoice => ({ ...invoice, id: nanoid() }));
    saveLocalInvoices([...localInvoices, ...newInvoices]);

    // Then sync to cloud if online and authenticated
    if (navigator.onLine && auth.currentUser) {
      try {
        const response = await api.post('/api/invoices/bulk', { invoices: newInvoices });
        return response.data;
      } catch (error) {
        // Queue for retry if offline or error
        queueOfflineOperation({
          type: 'bulk',
          endpoint: '/api/invoices/bulk',
          data: { invoices: newInvoices }
        });
        throw error;
      }
    }

    return {
      created: newInvoices,
      errors: []
    };
  },

  async getInvoiceStatus(id: string) {
    try {
      const response = await api.get(`/api/invoice-status/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get invoice status:', error);
      throw error;
    }
  }
};

// Settings API with local-first approach
export const userApi = {
  async getSettings() {
    if (navigator.onLine && auth.currentUser) {
      try {
        const response = await api.get<Settings>('/api/user/settings');
        const settings = response.data;
        saveLocalSettings(settings);
        return settings;
      } catch (error) {
        console.error('Failed to fetch settings:', error);
        return getLocalSettings();
      }
    }

    return getLocalSettings();
  },

  async updateSettings(settings: Partial<Settings>) {
    if (navigator.onLine && auth.currentUser) {
      try {
        // Force refresh latest invoice number from the server before updating
        let currentServerSettings;
        try {
          currentServerSettings = await this.getSettings();
        } catch {
          console.warn('Could not retrieve current server settings, continuing with update');
        }

        // Ensure we're not decreasing the invoice number
        if (settings.latestInvoiceNumber !== undefined || (currentServerSettings?.latestInvoiceNumber !== undefined)) {
          settings.latestInvoiceNumber = Math.max(
            settings.latestInvoiceNumber || 0,
            currentServerSettings?.latestInvoiceNumber || 0
          );
        }

        const response = await api.put('/api/user/settings', { settings });
        const updatedSettings = response.data;
        saveLocalSettings(updatedSettings);
        return updatedSettings;
      } catch (error) {
        // Queue for retry if offline or error
        queueOfflineOperation({
          type: 'update',
          endpoint: '/api/user/settings',
          data: { settings }
        });
        throw error;
      }
    }

    return settings;
  }
};

// Logo API for handling logo operations
export const logoApi = {
  async uploadLogo(file: File) {
    if (!navigator.onLine || !auth.currentUser) {
      throw new Error('Authentication required');
    }

    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', file);

      // Use our standard axios instance with proper headers
      const response = await api.post('/api/logo/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Failed to upload logo:', error);
      throw error;
    }
  },

  async deleteLogo() {
    if (!navigator.onLine || !auth.currentUser) {
      throw new Error('Authentication required');
    }

    try {
      const response = await api.delete('/api/logo/delete');
      return response.data;
    } catch (error) {
      console.error('Failed to delete logo:', error);
      throw error;
    }
  }
};

// Schedule API with proper error handling
export const scheduleApi = {
  async getSchedules(activeOnly: boolean = false) {
    try {
      const url = `/api/schedules${activeOnly ? '?activeOnly=true' : ''}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Failed to get schedules:', error);
      throw error;
    }
  },

  async getSchedule(id: string) {
    try {
      const response = await api.get(`/api/schedules/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to get schedule ${id}:`, error);
      throw error;
    }
  },

  async getScheduleForInvoice(invoiceId: string) {
    try {
      const response = await api.get(`/api/schedules/invoice/${invoiceId}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to get schedule for invoice ${invoiceId}:`, error);
      throw error;
    }
  },

  async createSchedule(schedule: Schedule) {
    try {
      const response = await api.post('/api/schedules', schedule);
      return response.data;
    } catch (error) {
      console.error('Failed to create schedule:', error);
      throw error;
    }
  },

  async updateSchedule(id: string, schedule: Schedule) {
    try {
      const response = await api.put(`/api/schedules/${id}`, schedule);
      return response.data;
    } catch (error) {
      console.error(`Failed to update schedule ${id}:`, error);
      throw error;
    }
  },

  async deleteSchedule(id: string) {
    try {
      const response = await api.delete(`/api/schedules/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to delete schedule ${id}:`, error);
      throw error;
    }
  }
};

// Company API with proper error handling
export const companyApi = {
  async search(query: string = '', limit: number = 5) {
    try {
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }

      const response = await api.get('/api/companies/autosuggest', {
        params: { query, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to search companies:', error);
      return { companies: [], total: 0, query };
    }
  },

  async getAll() {
    try {
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }

      const response = await api.get('/api/companies');
      return response.data;
    } catch (error) {
      console.error('Failed to get companies:', error);
      return { companies: [], total: 0 };
    }
  },

  async create(company: CompanyData) {
    try {
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }

      const response = await api.post('/api/companies', { company });
      return response.data;
    } catch (error) {
      console.error('Failed to create company:', error);
      throw error;
    }
  },

  async update(id: string, company: CompanyData & { id: string }) {
    try {
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }

      const response = await api.put(`/api/companies/${id}`, { company });
      return response.data;
    } catch (error) {
      console.error('Failed to update company:', error);
      throw error;
    }
  },

  async get(id: string) {
    try {
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`/api/companies/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get company:', error);
      throw error;
    }
  },

  async delete(id: string) {
    try {
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }

      const response = await api.delete(`/api/companies/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete company:', error);
      throw error;
    }
  }
};

// Admin API with proper error handling
export const adminApi = {
  async checkStatus() {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        return { isAdmin: false };
      }

      const response = await api.get('/api/user/admin-status');
      return response.data;
    } catch (error) {
      console.error('Failed to check admin status:', error);
      return { isAdmin: false };
    }
  },

  async getUsers() {
    const response = await api.get('/api/admin/users');
    return response.data;
  },

  async getUser(uid: string) {
    const response = await api.get(`/api/admin/users/${uid}`);
    return response.data;
  },

  async getUserInvoices(uid: string, page = 1, limit = 10) {
    const response = await api.get(`/api/admin/users/${uid}/invoices?page=${page}&limit=${limit}`);
    return response.data;
  },

  async getUserCompanies(uid: string) {
    const response = await api.get(`/api/admin/users/${uid}/companies`);
    return response.data;
  },

  async getUserSettings(uid: string) {
    const response = await api.get(`/api/admin/users/${uid}/settings`);
    return response.data;
  },

  async getUserSchedules(uid: string) {
    const response = await api.get(`/api/admin/users/${uid}/schedules`);
    return response.data;
  },

  async getInvoiceEmailPreview(uid: string, invoiceId: string) {
    const response = await api.get(`/api/admin/users/${uid}/invoices/${invoiceId}/email-preview`);
    return response.data;
  },

  async getEmailQueue(limit = 100) {
    const response = await api.get(`/api/admin/email-queue?limit=${limit}`);
    return response.data;
  },

  async sendEmail(emailId: string) {
    const response = await api.post(`/api/admin/email-queue/${emailId}/send`);
    return response.data;
  },

  async requeueEmail(emailId: string) {
    const response = await api.post(`/api/admin/email-queue/${emailId}/requeue`);
    return response.data;
  },

  async downloadInvoicePDF(emailId: string) {
    const response = await api.get(`/api/admin/email-queue/${emailId}/pdf`, {
      responseType: 'arraybuffer'
    });
    return response.data;
  }
};

// Mollie API for OAuth integration
export const mollieApi = {
  async getAuthUrl() {
    try {
      if (!navigator.onLine || !auth.currentUser) {
        throw new Error('Authentication required');
      }

      const response = await api.get('/api/mollie/auth/url');
      return response.data;
    } catch (error) {
      console.error('Failed to get Mollie auth URL:', error);
      throw error;
    }
  }
};

// Add this check before adding event listeners
if (typeof window !== 'undefined') {
  window.addEventListener('online', processOfflineQueue);
}

export default api;